import { Alert, Box, Button, Card, CardContent, CircularProgress, TextField, Typography } from '@mui/material';
import React, { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { useApiFormError } from '../../../library/useApiFormError';
import { getVisibilityButton } from '../../../library/formsHelper';
import { AcceptInvitationFormValues, IInvitation } from '../../../library/models/invitation';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { alertStyles, cardStyles, fieldsBoxStyles, loadingBoxStyles, pageWrapperStyles, submitButtonStyles, subtitleStyles, titleStyles } from './acceptInvitationCard.styles';
import { createAcceptInvitationSchema } from './acceptInvitationSchema';

interface AcceptInvitationCardProps {
    /** The code from the emailed link. */
    activationCode: string | undefined;
    onGoToLogin: () => void;
}

const DEFAULT_VALUES: AcceptInvitationFormValues = {
    password: '',
    confirmPassword: '',
};

/**
 * Where an invitation link lands.
 *
 * Four things it can draw: the form, the confirmation after accepting, "already active"
 * for a link that was accepted before, and "invalid" for everything else.
 *
 * Two calls, not one. The GET reads the invitation without spending it, so the clinic can
 * be named and the screen can tell whether a password is needed before the recipient
 * commits to anything; only the POST consumes the code. The previous version posted
 * immediately with no body, which failed for every newly invited person -- the API
 * requires a password whenever the account has never been claimed.
 *
 * Uses no router hooks, so it renders on its own in a test; the route shell supplies the
 * code and the way back to sign-in.
 */
export const AcceptInvitationCard = (props: AcceptInvitationCardProps): JSX.Element => {
    const { t: translate } = useTranslation();

    const [isAccepted, setIsAccepted] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { data: invitation, isLoading, isError, refetch } = useReactQuery<IInvitation>(
        services.authService.describeInvitation(props.activationCode)
    );

    const acceptInvitation = useReactMutation(
        services.authService.acceptInvitationMutation(props.activationCode)
    );

    const passwordRequired = invitation?.passwordRequired ?? false;

    const schema = React.useMemo(
        () => createAcceptInvitationSchema(translate, passwordRequired),
        [translate, passwordRequired]
    );

    const {
        register, handleSubmit, setError, formState: { errors, isValid, isSubmitting },
    } = useValidatedForm<AcceptInvitationFormValues>(schema, { defaultValues: DEFAULT_VALUES });

    const knownFields: string[] = React.useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<AcceptInvitationFormValues>(
        setError, knownFields, translate, ResourcesIds.INVITATION_ACCEPT_ERROR);

    const onValid = async (values: AcceptInvitationFormValues): Promise<void> => {
        clear();

        try {
            // undefined rather than {} when no password is wanted: the body is optional,
            // and sending a password the API did not ask for is refused outright.
            await acceptInvitation.mutateAsync(
                passwordRequired ? { password: values.password } : undefined
            );
            setIsAccepted(true);
        } catch (error: unknown) {
            report(error);
            // Asked again because the commonest refusal is that the invitation was spent
            // in the meantime -- a second tab, a second tap -- and then the honest screen
            // is "already active", not this form with an error above it.
            refetch();
        }
    };

    const renderBody = (): JSX.Element => {
        // Before anything the query says. Accepting spends the code, so the next refetch
        // -- and there is one on every window focus -- no longer describes an invitation
        // that can be accepted, and would replace this confirmation with whatever that
        // answer draws.
        if (isAccepted) {
            return (
                <>
                    <Typography variant="h5" sx={titleStyles}>
                        {translate(ResourcesIds.INVITATION_ACCEPTED_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={subtitleStyles}>
                        {translate(ResourcesIds.INVITATION_ACCEPTED_BODY)}
                    </Typography>
                    {/* An explicit step rather than an automatic redirect, so the
                        confirmation can actually be read. */}
                    <Button fullWidth variant="contained" onClick={props.onGoToLogin} sx={submitButtonStyles}>
                        {translate(ResourcesIds.GO_TO_LOGIN)}
                    </Button>
                </>
            );
        }

        if (isLoading) {
            return (
                <Box sx={loadingBoxStyles}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                        {translate(ResourcesIds.INVITATION_LOADING)}
                    </Typography>
                </Box>
            );
        }

        // Any failure means the link is no good: unknown, expired, withdrawn and malformed
        // codes all come back as errors, and none of them is worth telling apart on
        // screen -- the answer is the same in every case.
        if (isError || !invitation) {
            return (
                <>
                    <Typography variant="h5" sx={titleStyles}>
                        {translate(ResourcesIds.INVITATION_INVALID_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={subtitleStyles}>
                        {translate(ResourcesIds.INVITATION_INVALID_BODY)}
                    </Typography>
                    <Button fullWidth variant="outlined" onClick={props.onGoToLogin}>
                        {translate(ResourcesIds.GO_TO_LOGIN)}
                    </Button>
                </>
            );
        }

        // Compared against the one value rather than against READY, so an answer with no
        // status at all -- an older API -- still draws the form it always drew.
        if (invitation.status === 'ALREADY_ACCEPTED') {
            return (
                <>
                    <Typography variant="h5" sx={titleStyles}>
                        {translate(ResourcesIds.INVITATION_ALREADY_ACCEPTED_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={subtitleStyles}>
                        {translate(ResourcesIds.INVITATION_ALREADY_ACCEPTED_BODY)}
                    </Typography>
                    <Button fullWidth variant="contained" onClick={props.onGoToLogin} sx={submitButtonStyles}>
                        {translate(ResourcesIds.GO_TO_LOGIN)}
                    </Button>
                </>
            );
        }

        return (
            <>
                <Typography variant="h5" sx={titleStyles}>
                    {translate(
                        passwordRequired ? ResourcesIds.INVITATION_CLAIM_TITLE : ResourcesIds.INVITATION_JOIN_TITLE,
                        { clinicName: invitation.clinicName }
                    )}
                </Typography>
                <Typography variant="body2" sx={subtitleStyles}>
                    {translate(
                        passwordRequired ? ResourcesIds.INVITATION_CLAIM_SUBTITLE : ResourcesIds.INVITATION_JOIN_SUBTITLE,
                        { clinicName: invitation.clinicName }
                    )}
                </Typography>

                {formError && <Alert severity="error" sx={alertStyles}>{formError}</Alert>}

                <form onSubmit={handleSubmit(onValid)} noValidate>
                    {passwordRequired && (
                        <Box sx={fieldsBoxStyles}>
                            <TextField
                                fullWidth
                                disabled
                                label={translate(ResourcesIds.EMAIL_LABEL)}
                                value={invitation.emailAddress ?? ''}
                            />

                            <TextField
                                {...register('password')}
                                fullWidth
                                autoFocus
                                type={showPassword ? 'text' : 'password'}
                                label={translate(ResourcesIds.PASSWORD_LABEL)}
                                {...fieldProps(errors, 'password')}
                                slotProps={{
                                    input: {
                                        endAdornment: getVisibilityButton(
                                            showPassword,
                                            () => setShowPassword(!showPassword)
                                        ),
                                    },
                                }}
                            />

                            <TextField
                                {...register('confirmPassword')}
                                fullWidth
                                type={showConfirmPassword ? 'text' : 'password'}
                                label={translate(ResourcesIds.CONFIRM_PASSWORD_LABEL)}
                                {...fieldProps(errors, 'confirmPassword')}
                                slotProps={{
                                    input: {
                                        endAdornment: getVisibilityButton(
                                            showConfirmPassword,
                                            () => setShowConfirmPassword(!showConfirmPassword)
                                        ),
                                    },
                                }}
                            />
                        </Box>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={!isValid || isSubmitting}
                        sx={submitButtonStyles}
                    >
                        {translate(ResourcesIds.INVITATION_ACCEPT_BUTTON)}
                    </Button>
                </form>
            </>
        );
    };

    return (
        <Box sx={pageWrapperStyles}>
            <Card sx={cardStyles}>
                <CardContent>{renderBody()}</CardContent>
            </Card>
        </Box>
    );
};
